// Перенос улучшения, выкуп проданного и «Надеть лучшее» (E05-T16, ux/05 §4, §6, §9). Ступени, цена, потери и
// характеристики после переноса — только из GET /inventory/transfer/preview: клиент их не считает. Сравнение
// «Надеть лучшее» с надетой улучшенной деталью — по силе новой детали ПОСЛЕ переноса (сценарий 01 §12).
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { RARITY_NAMES, STAT_NAMES, itemRequiredLevel, itemName } from '../content/catalog.js';
import type { Item, Rarity, Slot } from '../content/types.js';
import { amount, num } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { inventory, online, player, saveCache, serverNow, setInventoryItems, setWallet, wallet } from '../state/game.js';
import type { SheetProps } from '../ui/App.js';
import { Dialog, ItemTile, useNow } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { HoldButton } from './store-ui.js';

type Preview = ResponseOf<Api['transferPreview']>;
type Sale = ResponseOf<Api['getSales']>['items'][number];
const RANK: Record<Rarity, number> = { common: 0, good: 1, rare: 2, epic: 3, legendary: 4 };

/** Сила детали для сравнения — та же оценка, что «Справность» на SCR-CHAR (ux/05 §1.1). */
export function power(stats: Item['stats']): number {
  return (stats.attack ?? 0) * 2 + (stats.hp ?? 0) * 0.5 + (stats.def ?? 0) * 3 + ((stats.crit ?? 0) + (stats.dodge ?? 0)) * 1000;
}

export interface Donor { item: Item; blocked: boolean }

/** Доноры для детали (ux/05 §9): тот же слот, улучшение > 0; редкостью выше новой — серым с причиной; по убыванию улучшения. */
export function donorsFor(target: Item, items: Item[]): Donor[] {
  return items
    .filter((i) => i.itemId !== target.itemId && i.slot === target.slot && i.upgradeLevel > 0)
    .sort((a, b) => b.upgradeLevel - a.upgradeLevel)
    .map((i) => ({ item: i, blocked: RANK[i.rarity] > RANK[target.rarity] }));
}

/**
 * «Надеть лучшее»: по каждому слоту — кандидат сильнее надетого. Если надетая улучшена и перенос на кандидата
 * возможен, кандидат сравнивается своей силой после переноса (`after[itemId]` — statsAfter из превью сервера);
 * без превью улучшенную надетую не меняем на «голую» деталь, которая слабее её.
 */
export function bestLoadout(items: Item[], after: Record<string, Item['stats']> = {}): Item[] {
  const out: Item[] = [];
  const level = player.value?.level ?? 100;
  for (const slot of ['head', 'jacket', 'pants', 'shoes', 'tool', 'accessory'] as Slot[]) {
    const worn = items.find((i) => i.slot === slot && i.equipped);
    const cands = items.filter((i) => i.slot === slot && !i.equipped && itemRequiredLevel(i) <= level);
    let best: Item | null = null;
    let bestP = worn ? power(worn.stats) : -1;
    for (const c of cands) {
      const canMove = !!worn && worn.upgradeLevel > 0 && RANK[worn.rarity] <= RANK[c.rarity];
      const p = canMove && after[c.itemId] ? power(after[c.itemId]!) : power(c.stats);
      if (p > bestP) { best = c; bestP = p; }
    }
    if (best) out.push(best);
  }
  return out;
}

/** Строка «Можно перенести +{n} с „{донор]“» и кнопка в MOD-ITEM (ux/05 §9). */
export function TransferOffer(p: { item: Item }) {
  const app = useApp();
  const items = inventory.value?.items ?? [];
  const donors = donorsFor(p.item, items);
  const ok = donors.filter((d) => !d.blocked);
  if (!donors.length || p.item.upgradeLevel > 0) return null;
  const top = ok[0];
  return (
    <div class="card col transfer-offer" style={{ gap: 4 }} data-testid="transfer-offer">
      {top ? <span>{tr('CL-TRANSFER-OFFER', { n: top.item.upgradeLevel, item: itemName(top.item.defId) })}</span> : null}
      {donors.length > 1 || !top ? (
        <div class="col" style={{ gap: 4 }}>
          {donors.map((d) => (
            <button key={d.item.itemId} class="list-row" disabled={d.blocked || !online.value} data-testid={`donor-${d.item.itemId}`}
              onClick={() => app.nav.openSheet('MOD-TRANSFER', { fromItemId: d.item.itemId, toItemId: p.item.itemId })}>
              <span style={{ flex: 1 }}>{itemName(d.item.defId)} +{d.item.upgradeLevel}</span>
              <span class="small muted">{d.blocked ? tr('CL-TRANSFER-RARITY') : RARITY_NAMES[d.item.rarity]}</span>
            </button>
          ))}
        </div>
      ) : (
        <button class="btn small accent" data-testid="transfer-open" disabled={!online.value} onClick={() => app.nav.openSheet('MOD-TRANSFER', { fromItemId: top.item.itemId, toItemId: p.item.itemId })}>
          {tr('CL-TRANSFER-OPEN')}
        </button>
      )}
    </div>
  );
}

function StatsDiff(p: { before: Item['stats']; after: Item['stats'] }) {
  const keys = (['attack', 'hp', 'def', 'crit', 'critMult', 'dodge'] as const).filter((k) => p.before[k] !== undefined || p.after[k] !== undefined);
  const f = (k: typeof keys[number], v: number) => (k === 'crit' || k === 'dodge' ? `${(v * 100).toFixed(1)}%` : k === 'critMult' ? `×${v.toFixed(2)}` : num(v));
  return (
    <div class="col" style={{ gap: 2 }} data-testid="transfer-stats">
      {keys.map((k) => (
        <div key={k} class="row" style={{ justifyContent: 'space-between' }}>
          <span>{STAT_NAMES[k]}</span>
          <span>{f(k, p.before[k] ?? 0)} → <b class="pos">{f(k, p.after[k] ?? 0)}</b></span>
        </div>
      ))}
    </div>
  );
}

/** MOD-TRANSFER ⛔: превью сервера, потери красным, удержание 1 с, если ступени пропадают. */
export function TransferDialog({ entry }: SheetProps) {
  const app = useApp();
  const fromId = String(entry.params['fromItemId']);
  const toId = String(entry.params['toItemId']);
  const items = inventory.value?.items ?? [];
  const from = items.find((i) => i.itemId === fromId);
  const to = items.find((i) => i.itemId === toId);
  const [pv, setPv] = useState<Preview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [n, setN] = useState(0);
  const key = useRef(app.idem());
  useEffect(() => {
    let alive = true;
    setErr(null);
    app.call('transferPreview', { query: { fromItemId: fromId, toItemId: toId } }).then((r) => { if (alive) setPv(r); })
      .catch((e: unknown) => { if (alive) setErr(e instanceof ApiCallError ? e.code : 'INTERNAL'); });
    return () => { alive = false; };
  }, [n]);
  const nuts = wallet.value?.nuts ?? 0;
  const lack = pv ? Math.max(0, pv.costNuts - nuts) : 0;
  const go = async () => {
    if (!pv || !from || !to) return;
    try {
      const r = await app.call('transferItem', { body: { fromItemId: fromId, toItemId: toId, expectedCostNuts: pv.costNuts }, idempotencyKey: key.current });
      key.current = app.idem();
      setWallet(r.wallet);
      setInventoryItems((xs) => xs.map((x) => (x.itemId === r.from.itemId ? r.from : x.itemId === r.to.itemId ? r.to : x)));
      // Донор был надет, новая — в сумке: новая надевается сама, донор уходит в сумку (ux/05 §9).
      if (from.equipped && !r.to.equipped) {
        const eq = await app.call('equip', { body: { itemId: r.to.itemId } }).catch(() => null);
        if (eq) setInventoryItems((xs) => xs.map((x) => ({ ...x, equipped: eq.equipped.some((e) => e.itemId === x.itemId) })));
      }
      saveCache();
      app.haptic('success');
      app.nav.openSheet('MOD-ITEM', { itemId: r.to.itemId }, { replace: true });
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      if (code !== 'NETWORK' && code !== 'TIMEOUT') key.current = app.idem();
      app.haptic('error');
      if (code === 'STALE_ITEM') { showToast(tr('CL-TRANSFER-STALE'), 'error'); void app.call('getInventory').then((d) => { inventory.value = d; saveCache(); }).catch(() => {}); setN((x) => x + 1); return; }
      if (code === 'NOT_ENOUGH_NUTS') { void app.call('getWallet').then(setWallet).catch(() => {}); setN((x) => x + 1); showToast(tr('CL-TRANSFER-NUTS'), 'error'); return; }
      if (code === 'TRANSFER_NOT_ALLOWED') { showToast(tr('CL-TRANSFER-NOT-ALLOWED'), 'error'); return; }
      if (code === 'IN_BATTLE') { showToast(tr('CL-RESPEC-BATTLE'), 'error'); return; }
      app.handleError(e, { resource: tr('CL-TRANSFER-SAFE') });
    }
  };
  const cancel = { text: tr('CL-CAR-CANCEL'), tone: 'outline' as const, testId: 'transfer-cancel', onClick: () => app.nav.popSheet() };
  if (!from || !to) return <Dialog title={tr('CL-TRANSFER-TITLE')} testId="MOD-TRANSFER" text={tr('CL-TRANSFER-GONE')} actions={[cancel]} />;
  return (
    <Dialog title={tr('CL-TRANSFER-TITLE')} testId="MOD-TRANSFER" actions={[cancel]}>
      <div class="col" style={{ gap: 8, marginBottom: 12 }} data-testid="MOD-TRANSFER-body">
        <div class="transfer-pair">
          <span class="col" style={{ alignItems: 'center' }}><ItemTile item={from} testId="transfer-from" /><span class="small">+{from.upgradeLevel} → +0</span></span>
          <span class="transfer-arrow" aria-hidden>→</span>
          <span class="col" style={{ alignItems: 'center' }}><ItemTile item={to} testId="transfer-to" /><span class="small pos">+{to.upgradeLevel} → +{pv ? to.upgradeLevel + pv.steps : '…'}</span></span>
        </div>
        {err ? <div class="banner" role="alert">{err === 'TRANSFER_NOT_ALLOWED' ? tr('CL-TRANSFER-NOT-ALLOWED') : tr('CL-TRANSFER-ERR')}<span class="spacer" /><button class="btn small outline" onClick={() => setN((x) => x + 1)}>{tr('CL-UI-KIT-15')}</button></div> : null}
        {!pv && !err ? <div class="skeleton" style={{ height: 96 }} /> : null}
        {pv ? (
          <>
            <span data-testid="transfer-steps">{tr('CL-TRANSFER-STEPS', { n: pv.steps })}</span>
            <span class={lack ? 'neg' : ''} data-testid="transfer-cost">{tr('CL-TRANSFER-COST', { s: amount('nuts', pv.costNuts) })}{lack ? ` · ${tr('CL-TRANSFER-LACK', { n: num(lack) })}` : ''}</span>
            {pv.masteryMoved ? <span class="small">{tr('CL-TRANSFER-MASTERY', { m: pv.masteryMoved })}</span> : null}
            {pv.lostSteps ? <b class="neg" data-testid="transfer-lost">{tr('CL-TRANSFER-LOST', { n: pv.lostSteps })}</b> : null}
            <span class="small muted">{tr('CL-TRANSFER-DONOR-ZERO', { item: itemName(from.defId) })}</span>
            {from.equipped && !to.equipped ? <span class="small">{tr('CL-TRANSFER-AUTO-EQUIP')}</span> : null}
            <StatsDiff before={to.stats} after={pv.statsAfter} />
            <HoldButton
              text={!online.value ? tr('UI-OFFLINE') : lack ? tr('CL-TRANSFER-LACK', { n: num(lack) }) : tr('CL-TRANSFER-CTA', { n: num(pv.costNuts) })}
              hold={pv.lostSteps > 0} disabled={!online.value || lack > 0} testId="transfer-go" onDone={go} />
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Блок «Продано за 24 ч» в SCR-INVENTORY: «Выкупить» за ту же цену, пропадает по `buybackUntil` сервера. */
export function SoldBlock(p: { sales: Sale[]; onChange: () => void }) {
  const app = useApp();
  useNow(30_000);
  const live = p.sales.filter((s) => Date.parse(s.buybackUntil) > serverNow());
  if (!live.length) return <div class="empty" data-testid="sold-empty">{tr('CL-SOLD-EMPTY')}</div>;
  const buy = async (s: Sale) => {
    try {
      const r = await app.call('buybackItem', { body: { itemId: s.item.itemId }, idempotencyKey: app.idem() });
      setWallet(r.wallet);
      setInventoryItems((xs) => [...xs.filter((x) => x.itemId !== r.item.itemId), r.item]);
      saveCache();
      app.haptic('success');
      showToast(tr('CL-SOLD-BACK', { item: itemName(r.item.defId) }), 'success');
      p.onChange();
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      app.haptic('error');
      const text: Record<string, string> = { GONE: 'CL-SOLD-GONE', ALREADY_PURCHASED: 'CL-SOLD-ALREADY', NOT_ENOUGH_NUTS: 'CL-SOLD-NUTS', NOT_FOUND: 'CL-SOLD-GONE' };
      if (text[code]) { showToast(tr(text[code]!), 'error'); p.onChange(); return; }
      app.handleError(e, { resource: tr('CL-RESPEC-SAFE') });
    }
  };
  return (
    <div class="col sold-list" data-testid="sold-list">
      <span class="small muted">{tr('CL-SOLD-HINT')}</span>
      {live.map((s) => (
        <div key={s.item.itemId} class="list-row" data-testid={`sold-${s.item.itemId}`}>
          <span style={{ width: 56, flexShrink: 0 }}><ItemTile item={s.item} /></span>
          <span class="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
            <b>{itemName(s.item.defId)}{s.item.upgradeLevel ? ` +${s.item.upgradeLevel}` : ''}</b>
            <span class="small muted">{tr('CL-SOLD-UNTIL', { t: new Date(s.buybackUntil).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) })}</span>
          </span>
          <button class="btn small accent" data-testid={`buyback-${s.item.itemId}`} disabled={!online.value || (wallet.value?.nuts ?? 0) < s.nuts} onClick={() => { void buy(s); }}>
            {tr('CL-SOLD-BUY', { s: amount('nuts', s.nuts) })}
          </button>
        </div>
      ))}
    </div>
  );
}

/** Превью переноса для «Надеть лучшее»: по одному запросу на слот, где надетая улучшена. */
export async function previewsForBest(call: ReturnType<typeof useApp>['call'], items: Item[]): Promise<Record<string, Item['stats']>> {
  const out: Record<string, Item['stats']> = {};
  for (const slot of ['head', 'jacket', 'pants', 'shoes', 'tool', 'accessory'] as Slot[]) {
    const worn = items.find((i) => i.slot === slot && i.equipped);
    if (!worn || worn.upgradeLevel === 0) continue;
    const cand = items.filter((i) => i.slot === slot && !i.equipped && RANK[i.rarity] >= RANK[worn.rarity]).sort((a, b) => power(b.stats) - power(a.stats))[0];
    if (!cand) continue;
    const pv = await call('transferPreview', { query: { fromItemId: worn.itemId, toItemId: cand.itemId } }).catch(() => null);
    if (pv) out[cand.itemId] = pv.statsAfter;
  }
  return out;
}
